import {
  draftPOGroupingValues,
  recommendationGroupByValues,
  recommendationOptimizations,
  recommendationScheduleIntervalUnits,
  recommendationSortByValues,
  recommendationStatuses,
  recommendationUrgencies,
  type DraftPOGrouping,
  type GroupedRecommendations,
  type RecommendationGroupBy,
  type RecommendationListItem,
  type RecommendationOptimization,
  type RecommendationRule,
  type RecommendationScheduleIntervalUnit,
  type RecommendationSortBy,
  type RecommendationStatus,
  type RecommendationUrgency,
} from "@redotech/redo-inventory-common/purchase-order-recommendations";
import {
  productFamilyIdFormat,
  productIdFormat,
  purchaseOrderIdFormat,
  purchaseOrderRecommendationIdFormat,
  purchaseOrderRecommendationRuleIdFormat,
  supplierIdFormat,
} from "@redotech/redo-model/ids";
import { SortDirectionSchema } from "@redotech/redo-model/tables/table";
import { AdvancedFilterDataSchema } from "@redotech/redo-model/views/views";
import { plainDate } from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod/v4";

export const purchaseOrderRecommendationStatus = recommendationStatuses;
export const purchaseOrderRecommendationUrgency = recommendationUrgencies;
export const purchaseOrderRecommendationOptimization =
  recommendationOptimizations;
export const purchaseOrderRecommendationScheduleIntervalUnit =
  recommendationScheduleIntervalUnits;
export const purchaseOrderRecommendationGroupBy = recommendationGroupByValues;
export const purchaseOrderRecommendationDraftPOGrouping = draftPOGroupingValues;
export const purchaseOrderRecommendationSortBy = recommendationSortByValues;
export type PurchaseOrderRecommendationSortBy = RecommendationSortBy;
export type PurchaseOrderRecommendationDraftPOGrouping = DraftPOGrouping;
export type PurchaseOrderRecommendationOptimization =
  RecommendationOptimization;
export type PurchaseOrderRecommendationScheduleIntervalUnit =
  RecommendationScheduleIntervalUnit;
export type PurchaseOrderRecommendationStatus = RecommendationStatus;
export type PurchaseOrderRecommendationUrgency = RecommendationUrgency;
export type PurchaseOrderRecommendationGroupBy = RecommendationGroupBy;
export type PurchaseOrderRecommendationListItem = RecommendationListItem;
export type PurchaseOrderRecommendationRule = RecommendationRule;

const PurchaseOrderRecommendationListItem_ = z.object({
  id: purchaseOrderRecommendationIdFormat.schema,
  productId: productIdFormat.schema,
  productTitle: z.string(),
  sku: z.string().nullable(),
  productFamilyId: productFamilyIdFormat.schema.nullable(),
  productFamilyTitle: z.string(),
  productFamilyImageUrl: z.string().nullable(),
  supplierId: supplierIdFormat.schema,
  supplierName: z.string(),
  status: createEnum(purchaseOrderRecommendationStatus),
  urgency: createEnum(purchaseOrderRecommendationUrgency),
  suggestedQuantity: z.number().int().nonnegative(),
  unitCost: z.number().nullable(),
  currency: z.string(),
  riskPerDay: z.number().nullable(),
  riskUnitsPerDay: z.number().nullable(),
  orderByDate: plainDate().nullable(),
  projectedStockoutDate: plainDate().nullable(),
  snoozedUntil: z.date().nullable(),
  linkedPurchaseOrderId: purchaseOrderIdFormat.schema.nullable(),
  onHand: z.number().nullable(),
  onOrder: z.number().nullable(),
  coverDays: z.number().nullable(),
});
export const PurchaseOrderRecommendationListItem: z.ZodType<PurchaseOrderRecommendationListItem> =
  PurchaseOrderRecommendationListItem_;

const PurchaseOrderRecommendationRule_ = z.object({
  id: purchaseOrderRecommendationRuleIdFormat.schema,
  name: z.string(),
  enabled: z.boolean(),
  scopeAllSuppliers: z.boolean(),
  optimization: createEnum(purchaseOrderRecommendationOptimization).nullable(),
  coverageDays: z.number().int().positive().nullable(),
  urgency: z.array(createEnum(purchaseOrderRecommendationUrgency)).nullable(),
  spendCapFractional: z.number().int().nonnegative().nullable(),
  minLineCostFractional: z.number().int().nonnegative().nullable(),
  scheduleIntervalCount: z.number().int().nonnegative().nullable(),
  scheduleIntervalUnit: createEnum(
    purchaseOrderRecommendationScheduleIntervalUnit,
  ).nullable(),
  supplierIds: z.array(supplierIdFormat.schema),
  lastRunAt: z.date().nullable(),
  nextRunAt: z.date().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
});
export const PurchaseOrderRecommendationRule: z.ZodType<PurchaseOrderRecommendationRule> =
  PurchaseOrderRecommendationRule_;

const ruleInput = z.object({
  name: z.string().min(1),
  enabled: z.boolean().default(true),
  scopeAllSuppliers: z.boolean().default(true),
  optimization: createEnum(purchaseOrderRecommendationOptimization)
    .nullable()
    .default(null),
  coverageDays: z.number().int().positive().nullable().default(null),
  urgency: z
    .array(createEnum(purchaseOrderRecommendationUrgency))
    .nullable()
    .default(null),
  spendCapFractional: z.number().int().nonnegative().nullable().default(null),
  minLineCostFractional: z
    .number()
    .int()
    .nonnegative()
    .nullable()
    .default(null),
  scheduleIntervalCount: z
    .number()
    .int()
    .nonnegative()
    .nullable()
    .default(null),
  scheduleIntervalUnit: createEnum(
    purchaseOrderRecommendationScheduleIntervalUnit,
  )
    .nullable()
    .default(null),
  supplierIds: z.array(supplierIdFormat.schema).default([]),
});

const listRecommendationsInput = z.object({
  statuses: z
    .array(createEnum(purchaseOrderRecommendationStatus))
    .optional()
    .default(() => Array.from(recommendationStatuses)),
  search: z.string().optional(),
  advancedFiltersData: z.array(AdvancedFilterDataSchema).optional(),
  groupBy: createEnum(purchaseOrderRecommendationGroupBy).default("none"),
  page: z.number().int().min(0).optional(),
  pageSize: z.number().int().min(1).max(500).optional(),
  sortBy: createEnum(purchaseOrderRecommendationSortBy).optional(),
  sortDirection: SortDirectionSchema.optional(),
});

const PurchaseOrderRecommendationFamilyGroup = z.object({
  productFamilyId: productFamilyIdFormat.schema.nullable(),
  productFamilyTitle: z.string(),
  productFamilyImageUrl: z.string().nullable(),
  urgency: createEnum(purchaseOrderRecommendationUrgency),
  suggestedQuantity: z.number().int().nonnegative(),
  riskPerDay: z.number().nullable(),
  riskUnitsPerDay: z.number().nullable(),
  earliestOrderByDate: plainDate().nullable(),
  earliestProjectedStockoutDate: plainDate().nullable(),
  recommendationCount: z.number().int().nonnegative(),
  currency: z.string(),
  products: z.array(PurchaseOrderRecommendationListItem),
});

const PurchaseOrderRecommendationSupplierGroup = z.object({
  supplierId: supplierIdFormat.schema,
  supplierName: z.string(),
  productFamilies: z.array(PurchaseOrderRecommendationFamilyGroup),
});

const PurchaseOrderRecommendationUrgencyGroup = z.object({
  urgency: createEnum(purchaseOrderRecommendationUrgency),
  productFamilies: z.array(PurchaseOrderRecommendationFamilyGroup),
});

const GroupedPurchaseOrderRecommendations_ = z.discriminatedUnion("groupBy", [
  z.object({
    groupBy: z.literal("none"),
    variants: z.array(PurchaseOrderRecommendationListItem),
  }),
  z.object({
    groupBy: z.literal("product_family"),
    productFamilies: z.array(PurchaseOrderRecommendationFamilyGroup),
  }),
  z.object({
    groupBy: z.literal("supplier"),
    suppliers: z.array(PurchaseOrderRecommendationSupplierGroup),
  }),
  z.object({
    groupBy: z.literal("urgency"),
    urgencies: z.array(PurchaseOrderRecommendationUrgencyGroup),
  }),
]);
const GroupedPurchaseOrderRecommendations: z.ZodType<GroupedRecommendations> =
  GroupedPurchaseOrderRecommendations_;

export const listGroupedPurchaseOrderRecommendations = {
  name: "listGroupedPurchaseOrderRecommendations" as const,
  input: listRecommendationsInput,
  output: z.object({
    grouped: GroupedPurchaseOrderRecommendations,
    totalCount: z.number().int().nonnegative(),
    totalRecommendationCount: z.number().int().nonnegative(),
  }),
};

export const createPurchaseOrderRecommendationRule = {
  name: "createPurchaseOrderRecommendationRule" as const,
  input: ruleInput,
  output: z.object({ rule: PurchaseOrderRecommendationRule }),
};

export const updatePurchaseOrderRecommendationRule = {
  name: "updatePurchaseOrderRecommendationRule" as const,
  input: ruleInput.extend({
    ruleId: purchaseOrderRecommendationRuleIdFormat.schema,
  }),
  output: z.object({ rule: PurchaseOrderRecommendationRule }),
};

export const deletePurchaseOrderRecommendationRule = {
  name: "deletePurchaseOrderRecommendationRule" as const,
  input: z.object({ ruleId: purchaseOrderRecommendationRuleIdFormat.schema }),
  output: z.object({ success: z.boolean() }),
};

export const getPurchaseOrderRecommendationRules = {
  name: "getPurchaseOrderRecommendationRules" as const,
  input: z.null(),
  output: z.object({ rules: z.array(PurchaseOrderRecommendationRule) }),
};

export const runPurchaseOrderRecommendationRule = {
  name: "runPurchaseOrderRecommendationRule" as const,
  input: z.object({ ruleId: purchaseOrderRecommendationRuleIdFormat.schema }),
  output: z.object({ workflowId: z.string() }),
};

export const snoozePurchaseOrderRecommendations = {
  name: "snoozePurchaseOrderRecommendations" as const,
  input: z.object({
    recommendationIds: z.array(purchaseOrderRecommendationIdFormat.schema),
    snoozedUntil: z.date(),
  }),
  output: z.object({ success: z.boolean() }),
};

export const dismissPurchaseOrderRecommendations = {
  name: "dismissPurchaseOrderRecommendations" as const,
  input: z.object({
    recommendationIds: z.array(purchaseOrderRecommendationIdFormat.schema),
  }),
  output: z.object({ success: z.boolean() }),
};

export const draftPurchaseOrdersFromRecommendations = {
  name: "draftPurchaseOrdersFromRecommendations" as const,
  input: z.object({
    poGrouping: createEnum(purchaseOrderRecommendationDraftPOGrouping).default(
      "supplier",
    ),
    recommendationIds: z
      .array(purchaseOrderRecommendationIdFormat.schema)
      .min(1),
  }),
  output: z.object({
    purchaseOrders: z.array(
      z.object({
        purchaseOrderId: purchaseOrderIdFormat.schema,
        recommendationIds: z.array(purchaseOrderRecommendationIdFormat.schema),
        supplierId: supplierIdFormat.schema,
      }),
    ),
  }),
};
